

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, User, Briefcase, Clock, ArrowLeft, Award, Target, BarChart3 } from 'lucide-react';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { useAuth } from '@/context/AuthContext';

interface InterviewSession {
  id: string;
  candidate_name: string;
  candidate_email: string;
  status: string;
  started_at: string;
  ended_at: string;
  duration_seconds: number;
  created_at: string;
  job_posting: {
    id: string;
    title: string;
    company_name: string;
  };
  ai_evaluation: any;
}

function InterviewSessionsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('jobId');
  const candidateId = searchParams.get('candidateId');
  const { isRecruiterValue, organizationId } = useAuth();

  const [sessions, setSessions] = useState<InterviewSession[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSessions();
  }, [jobId, candidateId]);

  const fetchSessions = async () => {
    try {
      const { supabase } = await import('@/lib/supabase-client');

      let query = supabase
        .from('interview_sessions')
        .select(`
          *,
          job_posting:job_postings(id, title, company_name, organization_id)
        `)
        .order('created_at', { ascending: false });

      if (jobId) {
        query = query.eq('job_posting_id', jobId);
      }

      if (candidateId) {
        query = query.eq('user_id', candidateId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching sessions:', error);
        throw error;
      }

      let filteredSessions = data || [];

      if (isRecruiterValue && organizationId) {
        const { data: recruiterProfiles } = await supabase
          .from('user_profiles')
          .select('id')
          .eq('is_recruiter', true);

        const recruiterUserIds = recruiterProfiles?.map((profile: any) => profile.id) || [];

        filteredSessions = filteredSessions.filter((session: any) => {
          const isOrgJob = session.job_posting?.organization_id === organizationId;
          const isNotRecruiter = !session.user_id || !recruiterUserIds.includes(session.user_id);
          return isOrgJob && isNotRecruiter;
        });
      }

      filteredSessions.sort((a: any, b: any) => {
        const scoreA = a.ai_evaluation?.finalScore || a.ai_evaluation?.overall_score || 0;
        const scoreB = b.ai_evaluation?.finalScore || b.ai_evaluation?.overall_score || 0;
        return scoreB - scoreA;
      });

      console.log('Fetched sessions:', filteredSessions.length);
      setSessions(filteredSessions);
    } catch (error: any) {
      console.error('Error fetching sessions:', error);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'incomplete':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'started':
      case 'in_progress':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'scheduled':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'bg-green-100 text-green-700 border-green-200';
    if (score >= 60) return 'bg-blue-100 text-blue-700 border-blue-200';
    if (score >= 40) return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    return 'bg-red-100 text-red-700 border-red-200';
  };

  const sessionsWithScores = sessions.filter(s =>
    s.status === 'completed' && (s.ai_evaluation?.finalScore || s.ai_evaluation?.overall_score)
  );

  const avgScore = sessionsWithScores.length > 0
    ? Math.round(sessionsWithScores.reduce((acc, s) => acc + (s.ai_evaluation?.finalScore || s.ai_evaluation?.overall_score || 0), 0) / sessionsWithScores.length)
    : 0;

  const completedSessions = sessions.filter(s => s.status === 'completed').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-green-50/30 p-4 sm:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-10 bg-gray-200 rounded w-1/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/3"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 bg-white rounded-2xl shadow-sm"></div>
              ))}
            </div>
            <div className="grid grid-cols-1 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-40 bg-white rounded-2xl shadow-sm"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-green-50/30">
      <div className="max-w-7xl mx-auto p-4 sm:p-8 pt-20 sm:pt-12 pb-8 sm:pb-12">
        <div className="mb-8 sm:mb-12 animate-fade-in-up">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Interview Sessions</h1>
              <p className="text-gray-600">Track your interview practice history and performance</p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate(jobId ? `/jobs/${jobId}` : '/jobs')}
              className="w-full sm:w-auto border-gray-300 hover:border-gray-400"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to {jobId ? 'Job' : 'Jobs'}
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            <Card className="bg-white border-none shadow-sm hover:shadow-md transition-all">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Sessions</p>
                    <p className="text-3xl font-bold text-gray-900">{sessions.length}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-none shadow-sm hover:shadow-md transition-all">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Completed</p>
                    <p className="text-3xl font-bold text-gray-900">{completedSessions}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <Award className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-none shadow-sm hover:shadow-md transition-all">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Average Score</p>
                    <p className="text-3xl font-bold text-gray-900">{avgScore}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <Target className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

    
          </div>
        </div>

        {sessions.length === 0 ? (
          <Card className="p-12 text-center border-none shadow-sm bg-white animate-scale-in">
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No Interviews Yet</h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Start practicing by selecting a job position and beginning your first interview session
            </p>
            <Button
              onClick={() => navigate('/jobs')}
              size="lg"
              className="bg-green-600 hover:bg-green-700 shadow-lg"
            >
              <Briefcase className="w-5 h-5 mr-2" />
              Browse Job Openings
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {sessions.map((session, index) => (
              <Card
                key={session.id}
                className="group bg-white shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer animate-fade-in-up border-b-2 border-l-2 border-b-green-600 border-l-green-600 border-t border-r border-gray-200"
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => navigate(`/interviews/${session.id}`)}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      <div className="flex flex-wrap items-center gap-3">
                        <h3 className="text-xl font-bold text-gray-900 group-hover:text-green-600 transition-colors">
                          {session.candidate_name}
                        </h3>
                        {session.status === 'incomplete' && (
                          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(session.status)}`}>
                            Too Short (No Score)
                          </span>
                        )}
                        {(session.ai_evaluation?.finalScore || session.ai_evaluation?.overall_score) && (
                          <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-50 border-2 border-green-200">
                            <Award className="w-5 h-5 text-green-600" />
                            <span className="text-2xl font-bold text-green-700">
                              {session.ai_evaluation.finalScore || session.ai_evaluation.overall_score}%
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                        <div className="flex items-center text-gray-600">
                          <User className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" />
                          <span className="truncate">{session.candidate_email}</span>
                        </div>

                        <div className="flex items-center text-gray-600">
                          <Briefcase className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" />
                          <span className="truncate">{session.job_posting.title}</span>
                        </div>

                        <div className="flex items-center text-gray-600">
                          <Calendar className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" />
                          <span>{new Date(session.started_at || session.created_at).toLocaleDateString()}</span>
                        </div>

                        {session.duration_seconds > 0 && (
                          <div className="flex items-center text-gray-600">
                            <Clock className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" />
                            <span>{formatDuration(session.duration_seconds)}</span>
                          </div>
                        )}
                      </div>

                      <div className="text-sm text-gray-500">
                        <span className="font-medium">Company:</span> {session.job_posting.company_name}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 self-start lg:self-center">
                      <Button
                        variant="default"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/interviews/${session.id}`);
                        }}
                      >
                        View Details →
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function InterviewSessionsPageWrapper() {
  return (
    <ProtectedRoute>
      <InterviewSessionsPage />
    </ProtectedRoute>
  );
}
